import {Helmet} from '@dr.pogodin/react-helmet';
import {
  closeCurrentDialog,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, defaultTo, isNil} from 'ramda';
import {isEmptyString} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetEmployeeMechanicsQuery, useGetUserQuery} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useDeleteFastTrackIssueMutation,
  useGetDirectSaleQuery,
  useGetFastTrackIssueQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  createHeaderActions,
  useTenant,
  DetailTemplate,
  DetailTemplateHeader,
  printPDF,
  EntityHeaderFlag,
  getUserName,
  handleApiError,
  NavigationItem,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {IssueNotes} from './(sections)/IssueNotes/IssueNotes';
import {Overview} from './(sections)/Overview/Overview';
import {AssignReceivedBy} from './components/AssignReceivedBy';

export function FastTrackIssuesDetail() {
  const {fastTrackIssueId} = useWarehouseParams();
  const tenant = useTenant();
  const navigate = useNavigate();

  const currencyFormatter = useFormatCurrency();

  const formatDate = useDateTimeFormatter();

  const fastTrackQuery = useGetFastTrackIssueQuery(
    {fastTrackId: fastTrackIssueId},
    {skip: isNil(fastTrackIssueId) || isEmptyString(fastTrackIssueId)}
  );

  const headerDynamicActionsQuery = useGetWarehouseHeaderDynamicActionsQuery({
    resource: 'fast-track',
    id: fastTrackIssueId,
  });

  const directSaleVehicleQuery = useGetVehicleQuery(
    {
      vehicleId: fastTrackQuery.data?.originEntityHeader?.directSale?.vehicleId as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.originEntityHeader?.directSale?.vehicleId) ||
        isEmptyString(fastTrackQuery.data?.originEntityHeader?.directSale?.vehicleId),
    }
  );

  const serviceOrderVehicleQuery = useGetVehicleQuery(
    {
      vehicleId: fastTrackQuery.data?.originEntityHeader?.serviceOrder?.vehicleId as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.originEntityHeader?.serviceOrder?.vehicleId) ||
        isEmptyString(fastTrackQuery.data?.originEntityHeader?.serviceOrder?.vehicleId),
    }
  );

  const createdByUserQuery = useGetUserQuery(
    {
      id: fastTrackQuery.data?.createdBy as string,
    },
    {skip: isNil(fastTrackQuery.data?.createdBy) || isEmptyString(fastTrackQuery.data?.createdBy)}
  );

  const serviceCaseId = fastTrackQuery.data?.originEntityHeader?.serviceOrder?.serviceCaseId;
  const directSaleId = fastTrackQuery.data?.originEntityHeader?.directSale?.directSaleId;

  const serviceCaseQuery = useGetMetadaServiceCaseQuery(
    {serviceCaseId: serviceCaseId as string},
    {skip: isNil(serviceCaseId) || isNil(fastTrackQuery.data?.receivedBy)}
  );

  const directSaleQuery = useGetDirectSaleQuery(
    {directSaleId: directSaleId as string},
    {skip: isNil(directSaleId) || isNil(fastTrackQuery.data?.receivedBy)}
  );

  const authorizationProfileId = isNil(serviceCaseId)
    ? directSaleQuery.data?.authorizationProfileId
    : serviceCaseQuery.data?.authorizationProfileId;

  const mechanicsQuery = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: authorizationProfileId as string,
      isActive: true,
      withToOffer: true,
    },
    {
      skip:
        isNil(authorizationProfileId) ||
        isEmptyString(authorizationProfileId) ||
        isNil(fastTrackQuery.data?.receivedBy),
    }
  );

  const receivedByMechanic = mechanicsQuery.data?.find(
    (mechanic) => mechanic?.employeeId === fastTrackQuery.data?.receivedBy
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-fast-track', contextId: fastTrackIssueId},
    {skip: !fastTrackIssueId}
  );

  const [putHeaderAction, {isLoading: isHeaderActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation({fixedCacheKey: 'fast-track-header-action'});

  const [deleteFastTrackIssue, {isLoading: isDeletingFastTrackIssue}] =
    useDeleteFastTrackIssueMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateShort', parseDate(date));
  };

  const isLoading =
    fastTrackQuery.isLoading ||
    headerDynamicActionsQuery.isLoading ||
    directSaleVehicleQuery.isLoading ||
    serviceOrderVehicleQuery.isLoading ||
    createdByUserQuery.isLoading ||
    serviceCaseQuery.isLoading ||
    directSaleQuery.isLoading ||
    mechanicsQuery.isLoading;

  const hasError = fastTrackQuery.isError || headerDynamicActionsQuery.isError;

  const fastTrackTitle = `${i18n.t('page.warehouse.labels.fastTrackIssue')} ${
    fastTrackQuery.data?.number ?? ''
  }`;

  const fastTrackNumber = fastTrackQuery.data?.number ?? '';

  const fastTrackVariantNameParameter = fastTrackQuery.data?.originEntityHeader.directSale
    ? fastTrackQuery.data.originEntityHeader?.directSale?.variant.name
    : fastTrackQuery.data?.originEntityHeader?.serviceOrder?.variant.name;

  const fastTrackRegistrationPlateParameter =
    directSaleVehicleQuery.data?.state?.registrationPlate ??
    serviceOrderVehicleQuery.data?.state?.registrationPlate;

  const fastTrackDateCreatedParameter = `${i18n.t('general.labels.dateCreated')}: ${formatToShortDate(
    fastTrackQuery.data?.createdAt
  )}`;

  const fastTrackCreatedByParameter = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUserQuery.data)}`;

  const fastTrackReceivedAtParameter = `${i18n.t('general.labels.receivedAt')}: ${formatToShortDate(
    fastTrackQuery.data?.receivedAt
  )}`;

  const fastTrackReceivedByParameter = `${i18n.t('general.labels.receivedBy')}: ${defaultTo(EMPTY_PLACEHOLDER, receivedByMechanic?.name)}`;

  const headerParameters = {
    number: fastTrackNumber,
    variantName: fastTrackVariantNameParameter,
    registrationPlate: fastTrackRegistrationPlateParameter,
    createdAt: fastTrackDateCreatedParameter,
    createdBy: fastTrackCreatedByParameter,
    receivedAt: fastTrackReceivedAtParameter,
    receivedBy: fastTrackReceivedByParameter,
  };

  const priceWithVat = currencyFormatter(
    defaultTo(0, fastTrackQuery.data?.totalPrice?.withVat?.amount),
    defaultTo(tenant.tenantCurrency, fastTrackQuery.data?.totalPrice?.withVat?.currency),
    2
  );

  const priceWithoutVat = currencyFormatter(
    defaultTo(0, fastTrackQuery.data?.totalPrice?.withoutVat?.amount),
    defaultTo(tenant.tenantCurrency, fastTrackQuery.data?.totalPrice?.withoutVat?.currency),
    2
  );

  const headerFlags = match(fastTrackQuery.data?.state)
    .returnType<EntityHeaderFlag[]>()
    .with('COMPLETED', always([{colorScheme: 'green', label: i18n.t('general.labels.completed')}]))
    .with(
      'FAST_TRACK_ISSUE',
      always([{colorScheme: 'yellow', label: i18n.t('general.labels.fastTrackIssued')}])
    )
    .otherwise(always([{colorScheme: 'blue', label: i18n.t('general.labels.pending')}]));

  const handleReceiverSelection = (receiver: string) => {
    putHeaderAction({
      id: fastTrackIssueId,
      resource: 'fast-track',
      body: {
        actionKey: 'issue',
        originEntityHeaderType: fastTrackQuery.data?.originEntityHeader.directSale
          ? 'direct-sale'
          : 'service-case-order',
        receivedBy: receiver,
      },
    })
      .unwrap()
      .then(() => documentCountQuery.refetch())
      .then(() => closeCurrentDialog())
      .catch(handleApiError);
  };

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntFastTrack') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: fastTrackIssueId,
          originEntityPrintoutType: 'prntFastTrack',
        },
      })
        .unwrap()
        .then((data) => {
          const pdfUrl = data?.printout?.pdfUrl;

          if (pdfUrl) {
            printPDF(pdfUrl);
          } else {
            showNotification.error();
          }
        })
        .then(() => documentCountQuery.refetch())
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.fastTrackIssuesDetailDocuments, {
              params: {id: fastTrackIssueId},
            })
          );
        })
        .catch(handleApiError);
    }

    match(actionKey)
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t('entity.warehouse.actions.deleteEntity'),
          onConfirm: () =>
            deleteFastTrackIssue({fastTrackIds: [fastTrackIssueId]})
              .unwrap()
              .then(() => navigate(warehouseRoutes.fastTrackIssues))
              .catch(handleApiError),
        });
      })
      .with('issue', () => {
        openDialog(
          <AssignReceivedBy
            onReceiverSelect={handleReceiverSelection}
            fastTrackIssueId={fastTrackIssueId}
          />,
          {
            title: i18n.t('entity.warehouse.labels.selectPersonReceivingGoods'),
            size: 'small',
            scrollBehavior: 'outside',
          }
        );
      })
      .otherwise(() => {
        putHeaderAction({
          id: fastTrackIssueId,
          resource: 'fast-track',
          body: {
            actionKey,
            originEntityHeaderType: fastTrackQuery.data?.originEntityHeader.directSale
              ? 'direct-sale'
              : 'service-case-order',
          },
        })
          .unwrap()
          .then(() => documentCountQuery.refetch())
          .catch(handleApiError);
      });
  };

  const headerActions = createHeaderActions({
    actions: headerDynamicActionsQuery.data?.actions,
    callback: handleActionClick,
    isLoading: isHeaderActionLoading || isDeletingFastTrackIssue || isPostPrintoutOnDemandLoading,
  });

  const buildHeaderParameters = () => {
    const parameters = [
      headerParameters.number,
      headerParameters.variantName,
      headerParameters.registrationPlate,
      headerParameters.createdAt,
      headerParameters.createdBy,
    ];

    if (!isNil(fastTrackQuery.data?.receivedAt)) {
      parameters.push(headerParameters.receivedAt);
    }

    if (!isNil(fastTrackQuery.data?.receivedBy)) {
      parameters.push(headerParameters.receivedBy);
    }

    return parameters;
  };

  const header: DetailTemplateHeader = {
    isEntityLoading: fastTrackQuery.isFetching,
    title: fastTrackTitle,
    icon: 'custom/warehouse',
    parameters: buildHeaderParameters(),
    recordId: fastTrackQuery.data?.fastTrackId,
    resourceId: EntityResourceIds.fastTrackIssues,
    controls: ['ASSIGNEE'],
    flags: headerFlags,
    actions: headerActions,
    primaryParameter: defaultTo(undefined, priceWithVat),
    secondaryParameter: `${priceWithoutVat} ${i18n.t('general.labels.w/oVat')}`,
  };

  const navigation: NavigationItem[] = [
    {
      id: 'fast-track-issue-overview',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.fastTrackIssuesDetailOverview, {
        params: {id: fastTrackIssueId},
      }),
      content: (
        <Overview
          data-testid={testIds.warehouse.fastTrackIssuesDetailOverview('section.overview')}
        />
      ),
      'data-testid': testIds.warehouse.fastTrackIssuesDetailOverview('navigation.overview'),
    },
    {
      id: 'fast-track-issue-notes',
      label: i18n.t('page.warehouse.labels.issueNotes'),
      href: composePath(warehouseRoutes.fastTrackIssuesDetailIssueNotes, {
        params: {id: fastTrackIssueId},
      }),
      content: (
        <IssueNotes
          data-testid={testIds.warehouse.fastTrackIssuesDetailOverview('section.issueNotes')}
        />
      ),
      'data-testid': testIds.warehouse.fastTrackIssuesDetailOverview('navigation.IssueNotes'),
    },
    {
      id: 'fast-track-issue-documents',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.fastTrackIssuesDetailDocuments, {
        params: {id: fastTrackIssueId},
      }),
      content: (
        <Documents
          onDocumentAction={() => documentCountQuery.refetch()}
          data-testid={testIds.warehouse.fastTrackIssuesDetailOverview('section.documents')}
        />
      ),
      badge: !!documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.fastTrackIssuesDetailOverview('navigation.documents'),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.fastTrackIssue')} />
      <DetailTemplate
        key={generateHashFromObjects(fastTrackQuery.data)}
        isLoading={isLoading}
        isError={hasError}
        isLocked={isHeaderActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.fastTrackIssuesDetailOverview('page')}
      />
    </>
  );
}
